import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { socketService } from '../services/socketService';

export interface LogEntry {
  stage: string;
  ts: number;
  percent: number;
}

interface UploadStatePersisted {
  activeTaskId: string | null;
  uploadMode: 'claims' | 'policies';
  fileName: string;
  uploading: boolean;
  currentStage: string | null;
  currentPercent: number;
  stageLog: LogEntry[];
  uploadResult: any;
  startTime: number | null;
}

const STORAGE_KEY = 'claims_ai_upload_state';

const loadPersistedState = (): UploadStatePersisted => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Only consider it actively running if it was started in the last 15 minutes
      const isRecent = parsed.startTime && Date.now() - parsed.startTime < 15 * 60 * 1000;
      if (parsed.activeTaskId && isRecent) {
        return {
          ...parsed,
          uploading: false, // Don't show loading until verified by server
        };
      }
    }
  } catch (e) {
    console.error('[UploadContext] Error reading from localStorage:', e);
  }
  return {
    activeTaskId: null,
    uploadMode: 'claims',
    fileName: '',
    uploading: false,
    currentStage: null,
    currentPercent: 0,
    stageLog: [],
    uploadResult: null,
    startTime: null,
  };
};

interface UploadContextType {
  uploading: boolean;
  uploadMode: 'claims' | 'policies';
  setUploadMode: (mode: 'claims' | 'policies') => void;
  fileName: string;
  currentStage: string | null;
  currentPercent: number;
  stageLog: LogEntry[];
  uploadResult: any;
  activeTaskId: string | null;
  error: string | null;
  startTime: number | null;
  elapsed: number;
  uploadProgress: number;
  startClaimsUpload: (file: File) => Promise<void>;
  startPoliciesUpload: (files: File[]) => Promise<void>;
  resetUpload: () => void;
  clearError: () => void;
  setError: (error: string | null) => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const initial = useRef(loadPersistedState());

  const [activeTaskId, setActiveTaskId] = useState<string | null>(initial.current.activeTaskId);
  const [uploadMode, setUploadModeState] = useState<'claims' | 'policies'>(initial.current.uploadMode || 'claims');
  const [fileName, setFileName] = useState<string>(initial.current.fileName || '');
  const [uploading, setUploading] = useState<boolean>(false);
  const [currentStage, setCurrentStage] = useState<string | null>(initial.current.currentStage);
  const [currentPercent, setCurrentPercent] = useState<number>(initial.current.currentPercent || 0);
  const [stageLog, setStageLog] = useState<LogEntry[]>(initial.current.stageLog || []);
  const [uploadResult, setUploadResult] = useState<any>(initial.current.uploadResult);
  const [startTime, setStartTime] = useState<number | null>(initial.current.startTime);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState<number>(0);

  const isMonitoring = useRef(false);
  const elapsedTimer = useRef<NodeJS.Timeout | null>(null);

  // Sync state to localStorage
  useEffect(() => {
    try {
      const stateToSave: UploadStatePersisted = {
        activeTaskId,
        uploadMode,
        fileName,
        uploading,
        currentStage,
        currentPercent,
        stageLog,
        uploadResult,
        startTime,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      console.error('[UploadContext] Error saving to localStorage:', e);
    }
  }, [
    activeTaskId,
    uploadMode,
    fileName,
    uploading,
    currentStage,
    currentPercent,
    stageLog,
    uploadResult,
    startTime,
  ]);

  const setUploadMode = useCallback((mode: 'claims' | 'policies') => {
    setUploadModeState(mode);
  }, []);

  const addLog = useCallback((stage: string, percent: number) => {
    const safePercent = typeof percent === 'number' ? percent : 0;
    setCurrentStage(stage);
    setCurrentPercent(safePercent);

    setStageLog((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].stage === stage) {
        const newLog = [...prev];
        newLog[newLog.length - 1] = {
          ...newLog[newLog.length - 1],
          percent: safePercent,
          ts: Date.now(),
        };
        return newLog;
      }
      return [...prev, { stage, ts: Date.now(), percent: safePercent }];
    });
  }, []);

  const handleTaskCompletion = useCallback(
    (result: any, mode: 'claims' | 'policies', targetFileName: string) => {
      addLog('completed', 100);
      let formattedResult: any = result;
      if (mode === 'policies') {
        const totalBenefits =
          (result as any).results?.reduce((sum: number, r: any) => {
            const count = (r.plans || []).reduce(
              (acc: number, p: any) => acc + (p.benefits_count || 0),
              0
            );
            return sum + count;
          }, 0) || 0;

        formattedResult = {
          filename: targetFileName,
          records_processed: totalBenefits,
          is_bulk_policy: true,
          results: (result as any).results,
        };
      } else {
        formattedResult = {
          ...(result as any),
          filename: targetFileName,
        };
      }

      setUploadResult(formattedResult);
      setUploading(false);
      setActiveTaskId(null);
      isMonitoring.current = false;
      queryClient.invalidateQueries();
    },
    [addLog, queryClient]
  );

  const monitorTask = useCallback(
    async (taskId: string, mode: 'claims' | 'policies', targetFileName: string) => {
      if (isMonitoring.current) return;
      isMonitoring.current = true;

      try {
        setUploading(true);
        setActiveTaskId(taskId);

        // 1. Join Socket Room
        socketService.joinTask(taskId);

        // 2. Setup Socket Listeners
        socketService.onProgress((progress) => {
          addLog(progress.stage, progress.percent);
        });

        const timerRef = { current: null as any };
        let timeoutTimer: NodeJS.Timeout;

        const wsPromise = new Promise((resolve) => {
          socketService.onCompleted((res) => {
            resolve(res);
          });
          timeoutTimer = setTimeout(() => {
            // let fallback polling handle if WS times out
          }, 15 * 60 * 1000);
        });

        // 3. Fallback Polling
        const pollPromise = apiService.pollUntilDone(
          taskId,
          (progress) => {
            addLog(progress.stage, progress.percent);
          },
          timerRef,
          2000,
          450
        );

        const result = await Promise.race([wsPromise, pollPromise]);
        clearTimeout(timeoutTimer!);
        if (timerRef.current) clearTimeout(timerRef.current);

        handleTaskCompletion(result, mode, targetFileName);
      } catch (err: any) {
        if (err?.message !== 'Processing timed out. Please try again.') {
          setError(err?.message || 'Processing failed. Please check file format.');
          setUploading(false);
          setActiveTaskId(null);
          isMonitoring.current = false;
        }
      } finally {
        socketService.offAllHandlers();
        isMonitoring.current = false;
      }
    },
    [addLog, handleTaskCompletion]
  );

  // Resume running task on reload / navigation
  useEffect(() => {
    const resumeTask = async () => {
      if (activeTaskId && !uploadResult && !isMonitoring.current) {
        try {
          const status = await apiService.getUploadStatus(activeTaskId);
          if (status.status === 'SUCCESS') {
            handleTaskCompletion(status.result, uploadMode, fileName);
          } else if (status.status === 'FAILURE') {
            setError(status.error || 'Previous analysis failed.');
            setUploading(false);
            setActiveTaskId(null);
          } else if (status.status === 'PENDING' || status.status === 'STARTED' || status.status === 'PROGRESS') {
            setUploading(true);
            if (status.progress) {
              const p = status.progress as any;
              const pct = p.percent !== undefined ? p.percent : p.progress;
              addLog(p.stage || 'parsing', pct || 10);
            }
            monitorTask(activeTaskId, uploadMode, fileName);
          } else {
            setActiveTaskId(null);
            setUploading(false);
          }
        } catch (e) {
          setActiveTaskId(null);
          setUploading(false);
        }
      }
    };
    resumeTask();
  }, [activeTaskId, uploadResult, uploadMode, fileName, addLog, handleTaskCompletion, monitorTask]);

  // Elapsed Timer
  useEffect(() => {
    if (uploading && startTime) {
      elapsedTimer.current = setInterval(() => setElapsed(Date.now() - startTime), 500);
    } else {
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    }
    return () => {
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, [uploading, startTime]);

  const startClaimsUpload = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      setUploadResult(null);
      setStageLog([]);
      setUploadProgress(0);
      setCurrentStage('parsing');
      setCurrentPercent(5);
      const now = Date.now();
      setStartTime(now);
      setElapsed(0);
      setFileName(file.name);
      setUploadModeState('claims');
      addLog('parsing', 10);

      const { task_id } = await apiService.uploadFile(file, (pct) => setUploadProgress(pct));
      monitorTask(task_id, 'claims', file.name);
    } catch (err: any) {
      setError(err?.message || 'Claims upload and analysis failed.');
      setUploading(false);
      setCurrentStage(null);
      setCurrentPercent(0);
    }
  };

  const startPoliciesUpload = async (files: File[]) => {
    try {
      setUploading(true);
      setError(null);
      setUploadResult(null);
      setStageLog([]);
      setUploadProgress(0);
      setCurrentStage('parsing');
      setCurrentPercent(5);
      const now = Date.now();
      setStartTime(now);
      setElapsed(0);
      const names = files.map((f) => f.name).join(', ');
      setFileName(names);
      setUploadModeState('policies');
      addLog('parsing', 10);

      const { task_id } = await apiService.uploadBulkPolicies(files, (pct) => setUploadProgress(pct));
      monitorTask(task_id, 'policies', names);
    } catch (err: any) {
      setError(err?.message || 'Policy extraction failed. Please check document format.');
      setUploading(false);
      setCurrentStage(null);
      setCurrentPercent(0);
    }
  };

  const resetUpload = useCallback(() => {
    socketService.offAllHandlers();
    setUploading(false);
    setUploadResult(null);
    setError(null);
    setStageLog([]);
    setCurrentStage(null);
    setCurrentPercent(0);
    setActiveTaskId(null);
    setStartTime(null);
    setFileName('');
    setElapsed(0);
    setUploadProgress(0);
    isMonitoring.current = false;
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <UploadContext.Provider
      value={{
        uploading,
        uploadMode,
        setUploadMode,
        fileName,
        currentStage,
        currentPercent,
        stageLog,
        uploadResult,
        activeTaskId,
        error,
        startTime,
        elapsed,
        uploadProgress,
        startClaimsUpload,
        startPoliciesUpload,
        resetUpload,
        clearError,
        setError,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};
